import { Anchor, Button, Input } from "antd";
import { InputOTP } from "antd-input-otp";
import axios from "axios";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { AiOutlineLoading } from "react-icons/ai";
import { useQuery } from "react-query";
import { Link } from "react-router-dom";
import tr from "../i18n";

const COUNTRY_CODE_BY_LANG = {
  en: "+1",
  tr: "+90",
};
const resolveLocale = (origin) => (origin === "tr" ? "tr" : "en");

const LoginForm = (props) => {
  const [lang, setLang] = useState("en");
  const [countryCode, setCountryCode] = useState(COUNTRY_CODE_BY_LANG[lang] ?? "+1");
  const [selectedTenant, setSelectedTenant] = useState("");
  const [phoneNumber, setPhoneValue] = useState("");
  const [otpValue, setOtpValue] = useState(/** @type {string[]} */[]);

  const tenant = useQuery(
    ["GetTenantByHost"],
    () => {
      return axios.get(
        process.env.REACT_APP_PLANNER_API_END_POINT +
        `/tenant/by-host?host=${process.env.NODE_ENV === "development" ? "kardesler.grungard.ai" : window.location.hostname}`,
      );
    },
    { enabled: !selectedTenant },
  );

  const getFullPhone = () => {
    return `${countryCode.replace(/^\+/, "")}${phoneNumber}`;
  };

  const submitPhoneOnEnterKey = (event) => {
    if (event.keyCode === 13) {
      props.onSend({ phone: getFullPhone(), tenantId: selectedTenant.id });
    }
  };

  const submitOTPOnEnterKey = (event) => {
    if (event.keyCode === 13) {
      props.onSubmit(otpValue.join(""));
    }
  };

  useEffect(() => {
    const loadedTenant = tenant.data?.data?.tenant;
    if (!loadedTenant) return;

    setSelectedTenant(loadedTenant);

    const locale = resolveLocale(loadedTenant.settings?.origin);
    console.log("Loaded tenant:", loadedTenant, "Resolved locale:", locale);
    localStorage.setItem("locale", locale);
    setLang(locale);
    setCountryCode(COUNTRY_CODE_BY_LANG[locale]);
  }, [tenant.data]);

  return (
    <div className="w-full max-w-md">
      <div className="rounded-2xl border border-gray-100 bg-white/95 px-6 py-8">
        <div className="flex   gap-3 flex-wrap mb-6 min-h-[24px]">
          {tenant.isLoading && (
            <div className="animate-spin text-primary">
              <AiOutlineLoading />
            </div>
          )}
          {tenant.isError && <div className="text-gray-600 text-xs ">{tr("Something went wrong. Please contact the administrator.")}</div>}
        </div>

        {tenant.isSuccess && selectedTenant?.settings?.logoUrl && (
          <div className="flex mb-5">
            <img
              src={selectedTenant?.settings?.logoUrl}
              alt=""
              style={{
                backgroundColor: "white",
                borderRadius: 10,
                border: "solid 0.5px #269EB9",
                height: "auto",
                maxWidth: "170px",
                padding: 10,
              }}
              className="rounded-md overflow-hidden"
            />
          </div>
        )}

        <div>
          <p className="mb-2 mt-0 text-3xl font-semibold leading-tight text-primary">{tr("Welcome to Grüngard")}</p>
          <p className="text-sm text-gray-500 mb-6">{tr("Please enter your phone number to login")}</p>
        </div>

        {selectedTenant && (
          <div className="space-y-3">
            <label className="text-sm font-medium text-gray-700">{tr("Phone")}</label>
            <div className="flex flex-row text-left lg:text-left">
              <Input.Group compact className="w-full">
                <Input style={{ width: "50px" }} value={countryCode} onChange={(e) => setCountryCode(e.target.value)} onKeyDown={submitPhoneOnEnterKey} />
                <Input
                  style={{ width: "calc(100% - 50px)" }}
                  placeholder="5xx xxx xx xx"
                  value={phoneNumber}
                  onChange={(e) => setPhoneValue(e.target.value)}
                  onKeyDown={submitPhoneOnEnterKey}
                />
              </Input.Group>
            </div>
          </div>
        )}

        {!props.hasOTPSent && (
          <div className="flex flex-row text-left lg:text-left mt-6">
            <Button onClick={(e) => props.onSend({ phone: getFullPhone(), tenantId: selectedTenant.id })} type="primary" size="large" className="w-full">
              {tr("Send Code")}
            </Button>
          </div>
        )}

        {props.hasOTPSent && selectedTenant && (
          <div className="mt-6 space-y-4">
            <div className="text-sm font-medium text-gray-700">{tr("Enter the code")}</div>
            <input autoComplete="one-time-code" inputMode="numeric" type="hidden" onChange={(e) => setOtpValue(e.target.value.split(""))} />
            <InputOTP
              autoSubmit={(otpValue) => props.onSubmit(otpValue.join(""))}
              inputType="numeric"
              length={6}
              onChange={setOtpValue}
              value={otpValue}
              wrapperStyle={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                gap: "10px",
              }}
              inputStyle={{
                flex: 1,
                minWidth: "44px",
                maxWidth: "80px",
                height: "clamp(48px, 6vw, 60px)",
                fontSize: "20px",
                textAlign: "center",
                fontWeight: 600,
              }}
            />

            <div className="flex flex-row text-left lg:text-left">
              <Button onClick={(e) => props.onSubmit(otpValue.join(""))} type="primary" size="large" className="w-full">
                {tr("Submit")}
              </Button>
            </div>

            <div className="flex flex-row relative mb-1 justify-center">
              <Anchor>
                <Link to="#" title={tr("Send again")} onClick={(e) => props.onSend({ phone: getFullPhone(), tenantId: selectedTenant.id })}>
                  {tr("Send again")}
                </Link>
              </Anchor>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

LoginForm.propTypes = {
  onSend: PropTypes.func,
  hasOTPSent: PropTypes.bool,
  emailOrPhone: PropTypes.string,
  onSubmit: PropTypes.func,
};

export default LoginForm;
